/**
 * İÇERİK TAZELİĞİ — hangi içerik yaşlanıyor, önce hangisi yenilenmeli.
 * Skor = yaş (sitemap lastmod / yayın tarihi) × GSC 28 günlük trafik kıyası.
 * AI Overview'da alıntılanan bayat sayfalar en üstte ayrıca uyarılır
 * ("AI'a görünen yüzün eskiyor"). Her satırdan tek tıkla Sayfa Güçlendirme'ye.
 */
import { useMemo, useState } from "react";
import { Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { Leaf, Zap, Loader2, Sparkles, TrendingDown, ChevronDown, ChevronUp } from "lucide-react";

const C = { green: "#12b76a", red: "#d92d20", amber: "#f79009", purple: "#7a5af8" };

type Satir = { pageId: number; url: string; title: string | null; tip: string; yayinTarihi: string | null; guncellemeTarihi: string | null; tarihKaynagi: string | null; yasGun: number | null; yasEtiketi: string; skor: number; tikSon28: number | null; tikOnceki28: number | null; dususYuzde: number | null; aiAlintili: boolean; aiVitrinBayat: boolean; oncelik: number };
type Ozet = { makale: number; tarihliOran: number; ortYasGun: number | null; dagilim: { taze: number; yaslaniyor: number; bayat: number }; acil: number; toplamTik28: number | null };
type Veri = { gscBagli: boolean; ortalama: number | null; ozet?: Ozet; satirlar: Satir[]; aiVitrini: Satir[] };

const renk = (n: number) => (n >= 80 ? C.green : n >= 50 ? C.amber : C.red);
const yol = (u: string) => u.replace(/^https?:\/\/[^/]+/, "") || "/";

export default function Tazelik() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const { data: d, isLoading } = useQuery<Veri>({
    queryKey: ["tazelik", activeDomainId],
    enabled: !!activeDomainId,
    staleTime: 10 * 60_000,
    queryFn: async () => {
      const r = await fetch(`/api/domains/${activeDomainId}/tazelik`);
      if (!r.ok) throw new Error("tazelik");
      return r.json();
    },
  });

  type SirKol = "oncelik" | "yas" | "tik" | "degisim" | "skor";
  const [sira, setSira] = useState<{ k: SirKol; azalan: boolean }>({ k: "oncelik", azalan: true });
  const sirala = (k: SirKol) => setSira((o) => (o.k === k ? { k, azalan: !o.azalan } : { k, azalan: true }));
  const deger = (s: Satir, k: SirKol): number | null =>
    k === "oncelik" ? s.oncelik : k === "yas" ? s.yasGun : k === "tik" ? s.tikSon28 : k === "degisim" ? s.dususYuzde : s.skor;
  const siraliSatirlar = useMemo(() => {
    const satirlar = [...(d?.satirlar ?? [])];
    satirlar.sort((a, b) => {
      const va = deger(a, sira.k), vb = deger(b, sira.k);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;            // boş değerler hep sonda
      if (vb == null) return -1;
      return sira.azalan ? vb - va : va - vb;
    });
    return satirlar;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d?.satirlar, sira]);
  const Baslik = ({ k, ad, ortala }: { k: SirKol; ad: string; ortala?: boolean }) => (
    <th className={`cursor-pointer select-none px-2 py-2 hover:text-foreground ${ortala ? "text-center" : "text-left"}`} onClick={() => sirala(k)} title="Sıralamak için tıkla">
      <span className="inline-flex items-center gap-0.5">{ad}{sira.k === k && (sira.azalan ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />)}</span>
    </th>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/10"><Leaf className="h-6 w-6 text-brand-500" /></div>
        <div>
          <h1 className="text-xl font-bold">İçerik Tazeliği</h1>
          <p className="text-sm text-muted-foreground">Hangi içerik yaşlanıyor, trafiği eriyor — ve önce hangisi yenilenmeli. Sıralama: kaybedilecek trafiği en çok olan bayat sayfa üstte.</p>
        </div>
        {d?.ortalama != null && (
          <div className="ml-auto text-right">
            <div className="text-3xl font-bold tabular-nums" style={{ color: renk(d.ortalama) }}>{d.ortalama}<span className="text-sm text-muted-foreground">/100</span></div>
            <div className="text-[11px] text-muted-foreground">site tazelik ortalaması{!d.gscBagli && " · GSC bağlı değil (yaş bazlı)"}</div>
          </div>
        )}
      </div>

      {d?.ozet && d.satirlar.length > 0 && (() => { const o = d.ozet; const top = o.dagilim.taze + o.dagilim.yaslaniyor + o.dagilim.bayat || 1; return (
        <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Card><CardContent className="p-4">
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">İçerik Envanteri</div>
            <div className="mt-1 text-2xl font-bold tabular-nums">{o.makale}</div>
            <div className="text-[11px] text-muted-foreground">%{o.tarihliOran}'inin tarihi tespit edildi{o.toplamTik28 != null && ` · 28g ${o.toplamTik28} tık`}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Ortalama Yaş</div>
            <div className="mt-1 text-2xl font-bold tabular-nums">{o.ortYasGun != null ? `${o.ortYasGun}g` : "—"}</div>
            <div className="text-[11px] text-muted-foreground">{o.ortYasGun != null && o.ortYasGun > 365 ? "kütüphane yaşlanıyor" : "tarihli sayfalar üzerinden"}</div>
          </CardContent></Card>
          <Card className="col-span-2"><CardContent className="p-4">
            <div className="mb-1.5 flex items-center justify-between"><span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Tazelik Dağılımı</span>
              {o.acil > 0 && <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: `${C.red}18`, color: C.red }}>{o.acil} sayfa acil yenileme istiyor</span>}
            </div>
            <div className="flex h-3 overflow-hidden rounded-full" title={`taze ${o.dagilim.taze} · yaşlanıyor ${o.dagilim.yaslaniyor} · bayat ${o.dagilim.bayat}`}>
              {o.dagilim.taze > 0 && <div style={{ width: `${(o.dagilim.taze / top) * 100}%`, background: C.green }} />}
              {o.dagilim.yaslaniyor > 0 && <div style={{ width: `${(o.dagilim.yaslaniyor / top) * 100}%`, background: C.amber }} />}
              {o.dagilim.bayat > 0 && <div style={{ width: `${(o.dagilim.bayat / top) * 100}%`, background: C.red }} />}
            </div>
            <div className="mt-1 flex gap-3 text-[11.5px]"><span style={{ color: C.green }}>● taze {o.dagilim.taze}</span><span style={{ color: C.amber }}>● yaşlanıyor {o.dagilim.yaslaniyor}</span><span style={{ color: C.red }}>● bayat {o.dagilim.bayat}</span></div>
          </CardContent></Card>
        </div>
      ); })()}

      {d && d.aiVitrini.length > 0 && (
        <div className="mb-4 rounded-lg border px-4 py-3" style={{ borderColor: `${C.purple}55`, background: `${C.purple}0d` }}>
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold" style={{ color: C.purple }}><Sparkles className="h-4 w-4" /> AI vitrinin bayatlıyor — {d.aiVitrini.length} sayfa</div>
          <p className="text-[12.5px] text-muted-foreground">Bu sayfalar yapay zekâ cevaplarında <b>kaynak gösteriliyor</b> ama içerikleri eskiyor. AI motorları taze kaynağı tercih eder — bu alıntıları kaybetmeden yenile:</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {d.aiVitrini.slice(0, 6).map((s) => (
              <Link key={s.pageId} href={`/page-strengthen/${s.pageId}`}><span className="cursor-pointer rounded-full border px-2.5 py-0.5 font-mono text-[11px] hover:bg-white/50 dark:hover:bg-white/[0.06]" style={{ borderColor: `${C.purple}44` }}>{yol(s.url)}</span></Link>
            ))}
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="py-16 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />Sayfalar değerlendiriliyor…</div>
      ) : !d || d.satirlar.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Değerlendirilecek içerik sayfası yok — önce siteyi tarat.</CardContent></Card>
      ) : (
        <Card><CardContent className="p-4">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] border-collapse text-[12.5px]">
              <thead><tr className="border-b-2 border-gray-100 text-left dark:border-gray-800">
                <th className="px-2 py-2 text-left font-semibold">Sayfa</th>
                <th className="px-2 py-2 text-left font-semibold" title="Sayfanın ilk yayınlanma tarihi">Yayın</th>
                <Baslik k="yas" ad="Son Güncelleme" />
                <Baslik k="tik" ad="Tık (28g)" ortala />
                <Baslik k="degisim" ad="Değişim" ortala />
                <Baslik k="skor" ad="Tazelik" ortala />
                <th className="px-2 py-2 text-center" title="Yenileme önceliğine dön" onClick={() => sirala("oncelik")} style={{ cursor: "pointer" }}>AI</th>
                <th className="px-2 py-2" />
              </tr></thead>
              <tbody>
                {siraliSatirlar.slice(0, 60).map((s) => (
                  <tr key={s.pageId} className="border-b border-gray-50 dark:border-gray-800/60">
                    <td className="max-w-[300px] px-2 py-2"><div className="truncate font-mono text-[11.5px]" title={s.url}>{yol(s.url)}</div>{s.title && <div className="truncate text-[11px] text-muted-foreground" title={s.title}>{s.title}</div>}</td>
                    {/* Yayın ve son güncelleme AYRI sütunlar (kullanıcı isteği) — kritik olan son güncelleme */}
                    <td className="whitespace-nowrap px-2 py-2 text-[11.5px] text-muted-foreground">{s.yayinTarihi ?? "—"}</td>
                    <td className="whitespace-nowrap px-2 py-2 text-[11.5px]" title={s.tarihKaynagi ? `kaynak: ${s.tarihKaynagi}` : "tarih bulunamadı"}>
                      <div className="font-medium text-foreground/85">{s.guncellemeTarihi ?? s.yayinTarihi ?? "—"}</div>
                      <div className="text-[10.5px] text-muted-foreground">{s.yasEtiketi}{s.yasGun != null && <span className="opacity-60"> · {s.yasGun}g</span>}</div>
                    </td>
                    <td className="px-2 py-2 text-center tabular-nums">{s.tikSon28 ?? "—"}</td>
                    <td className="px-2 py-2 text-center tabular-nums">{s.dususYuzde == null ? "—" : <span className="inline-flex items-center gap-0.5" style={{ color: s.dususYuzde < -10 ? C.red : s.dususYuzde > 10 ? C.green : undefined }}>{s.dususYuzde < -10 && <TrendingDown className="h-3 w-3" />}%{s.dususYuzde}</span>}</td>
                    <td className="px-2 py-2 text-center"><span className="font-bold tabular-nums" style={{ color: renk(s.skor) }}>{s.skor}</span></td>
                    <td className="px-2 py-2 text-center">{s.aiAlintili && <Sparkles className="mx-auto h-3.5 w-3.5" style={{ color: s.aiVitrinBayat ? C.red : C.purple }} />}</td>
                    <td className="px-2 py-2"><Link href={`/page-strengthen/${s.pageId}`}><Button size="sm" variant="outline" className="h-7 gap-1 text-[11px]"><Zap className="h-3 w-3" /> Yenile</Button></Link></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">Tazelik = yaş tabanı × trafik çarpanı (GSC 28 gün kıyası). ✨ = AI cevaplarında kaynak gösteriliyor (kırmızıysa bayatlamış — öncelik ver). Yaş, sitemap lastmod ya da yayın tarihinden; ikisi de yoksa nötr sayılır.</p>
        </CardContent></Card>
      )}
    </div>
  );
}
